/**
 * @file MembersPage.js
 * @module src/js/pages/MembersPage
 * @description
 * **Page component to display members in the EDALab frontend.**
 * - Fetches and displays member contact information from the backend.
 * - Supports category-based filtering (Advisor, Post Doctoral, Ph.D., M.S., Graduates).
 * - Includes search functionality within the selected category.
 * - Implements pagination for better navigation through member lists.
 * - Allows logged-in users to add new members.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-08-17: Removed the increment grade button; grades are now incremented automatically on Aug. 1 by the backend. (cwyeh@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import AdvisorSections from "../components/AdvisorSections";
import CategoryPagination from '../components/CategoryPagination';
import NumberPagination from '../components/NumberPagination';
import PagedTable from '../components/PagedTable';
import SearchBar from "../components/SearchBar";
import SpanARow from "../components/SpanARow";
import { MailTo, StyledImg, TextWithBr } from "../components/UtilityComponents";

export default function MembersPage() {
    const navigate = useNavigate();
    const { uid } = useUser();
    const fetchFromBackend = useFetchFromBackend();
    const [currentCategory, setCurrentCategory] = useState("Ph.D.");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [members, setMembers] = useState([]);
    const [membersForCurrentCategory, setMembersForCurrentCategory] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);

    const numPageButtons = 10;
    const numItemsPerPage = 10;

    const categories = useMemo(() => [
        "Advisor",
        "Post Doctoral",
        "Ph.D.",
        "M.S.",
        "Graduates (Ph.D.)",
        "Graduates (M.S.)"
    ], []);

    const membersOnCurrentPage = useMemo(() => {
        if (!filteredMembers || filteredMembers.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredMembers.slice(start, end);
    }, [filteredMembers, currentPage]);

    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: `/api/members` });
            setMembers(data || []);
            setLoading(false);
        }
        fetchData();
    }, [fetchFromBackend]);

    useEffect(() => {
        const filtered = members.filter(member => {
            if (currentCategory === "Post Doctoral") {
                return member.group === "student" && member.level === 4;
            } else if (currentCategory === "Ph.D.") {
                return member.group === "student" && member.level === 2;
            } else if (currentCategory === "M.S.") {
                return member.group === "student" && (member.level === 1 || member.level === 3);
            } else if (currentCategory === "Graduates (Ph.D.)") {
                return member.group === "grad" && member.level === 2;
            } else if (currentCategory === "Graduates (M.S.)") {
                return member.group === "grad" && member.level === 1;
            } else {
                return false;
            }
        });
        setMembersForCurrentCategory(filtered);
    }, [members, currentCategory]);

    const categoryPagination = (
        <CategoryPagination
            categories={categories}
            currentCategory={currentCategory}
            setCurrentCategory={setCurrentCategory}
            headerRef={headerRef}
        />
    )

    const numberPagination = (
        <NumberPagination
            length={filteredMembers.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            currentCategory={currentCategory}
            headerRef={headerRef}
        />
    );

    // The reason for using useMemo here is to prevent unnecessary re-renders
    // You can find more details in the News.js component
    const filterKeys = useMemo(() => {
        if (['Post Doctoral', 'Ph.D.', 'M.S.'].includes(currentCategory)) {
            return ['name', 'mail', 'institute', 'year'];
        } else if (currentCategory.startsWith("Graduates")) {
            return ['name', 'mail', 'final_grad', 'grad_year'];
        } else {
            return null;
        }
    }, [currentCategory]);
    const searchBar = (
        <SearchBar
            data={membersForCurrentCategory}
            setFilteredData={setFilteredMembers}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            currentCategory={currentCategory}
            filterKeys={filterKeys}
        />
    );

    const table = currentCategory === "Advisor" ? <AdvisorSections typeList={[0]} /> : (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className='hori-vert-center' width='10%'>Photo</th>
                        <th className='hori-vert-center'>Name</th>
                        <th className='hori-vert-center'>{currentCategory.startsWith("Graduates") ? "Degree" : "Institute"}</th>
                        <th className='hori-vert-center'>{currentCategory.startsWith("Graduates") ?
                            <span>Graduation<br />Year</span> :
                            <span>Current<br />Grade</span>
                        }</th>
                        <th className='hori-vert-center'>E-mail</th>
                    </tr>
                </thead>
                <tbody>
                    {uid === 'Guest' && currentCategory !== "Advisor" ? (
                        <SpanARow
                            colSpan="5"
                            onClick={() => navigate(
                                '/login',
                                { state: { from: '/member-zone/new-member' } }
                            )}
                            text='🔑 Log in to add a new member'
                        />
                    ) : (
                        <SpanARow
                            colSpan="5"
                            onClick={() => navigate('/member-zone/new-member')}
                            text='👤 Add a new member'
                        />
                    )}
                    {uid === 'Guest' && currentCategory !== "Advisor" ? (
                        <SpanARow
                            colSpan="5"
                            onClick={() => navigate(
                                '/login',
                                { state: { from: '/member-zone/profile' } }
                            )}
                            text='🔑 Log in to edit your profile'
                        />
                    ) : (
                        <SpanARow
                            colSpan="5"
                            onClick={() => navigate('/member-zone/profile')}
                            text='👤 Edit your profile'
                        />
                    )}
                    {filteredMembers.length === 0 && (
                        <SpanARow
                            colSpan="5"
                            text="No members found"
                            loading={loading}
                        />
                    )}
                    {membersOnCurrentPage.map((item, i) => (
                        <tr key={i}>
                            <td className='hori-vert-center'>
                                <StyledImg
                                    src={item.photo !== 'none' ? item.photo_path : null}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        borderRadius: '5px',
                                        boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)'
                                    }}
                                />
                            </td>
                            <td className='hori-vert-center'><TextWithBr text={item.name} /></td>
                            <td className='hori-vert-center'>{currentCategory.startsWith("Graduates") ? item.final_grad : item.institute}</td>
                            <td className='hori-vert-center'>{currentCategory.startsWith("Graduates") ? item.grad_year : item.year}</td>
                            <td className='hori-vert-center'><MailTo mail={item.mail} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <PagedTable
            header="Members"
            headerRef={headerRef}
            categoryPagination={categoryPagination}
            numberPagination={(currentCategory !== "Advisor" &&
                filteredMembers.length > numItemsPerPage) ? numberPagination : null}
            table={table}
            searchBar={currentCategory === "Advisor" ? null : searchBar}
        />
    );
}
